'use client'

import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useCallback,
} from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion, type Variants } from 'motion/react'
import { Button, Skeleton } from '@material-bank/mb3-components'
import { useTranslations } from '@material-bank/i18n'
import './galleryViewer.css'
import { formatImageSource } from '@material-bank/mtag-utilities'
import { usePrefersReducedMotion } from '../morphDialog/usePrefersReducedMotion'

const REVEAL_TIMEOUT_MS = 600
const SCROLL_KEYS = new Set([
  'ArrowUp',
  'ArrowDown',
  'PageUp',
  'PageDown',
  'Home',
  'End',
  ' ',
])

type GalleryViewerProps = {
  items: string[]
  activeIndex?: number | null
  onClose?: () => void
}

export function GalleryViewer({
  items,
  activeIndex,
  onClose,
}: GalleryViewerProps) {
  const t = useTranslations('product.colorways')
  const prefersReducedMotion = usePrefersReducedMotion()
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set())
  const activeItemRef = useRef<HTMLElement>(null)
  const hasUserScrolledRef = useRef(false)
  const [revealTimedOut, setRevealTimedOut] = useState(false)

  const keyDownHandler = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose?.()
        return
      }

      if (SCROLL_KEYS.has(e.key)) {
        hasUserScrolledRef.current = true
      }
    },
    [onClose],
  )

  const isOpen = activeIndex !== null && activeIndex !== undefined
  // Images above the tapped one decide where it sits, so the list stays hidden
  // until they have their real heights, or a short timeout passes on a slow network.
  const isPlaced =
    !isOpen ||
    revealTimedOut ||
    items
      .slice(0, (activeIndex ?? 0) + 1)
      .every((item) => loadedImages.has(item))

  useEffect(() => {
    if (!isOpen) {
      return
    }

    document.addEventListener('keydown', keyDownHandler)
    document.body.style.overflow = 'hidden'

    // Deliberately does NOT restore `overflow` here: `isOpen` flips false the moment
    // the close begins, but the overlay stays mounted through its AnimatePresence
    // exit. Releasing the lock now would let the body scroll behind a scrim that is
    // still fully covering the screen. The release is gated on exit-complete below.
    return () => {
      document.removeEventListener('keydown', keyDownHandler)
    }
  }, [isOpen, keyDownHandler])

  // Opens already centred on the tapped image, and re-centres it before paint each
  // time an image loads (a loaded image above it changes height) until the user
  // scrolls. Scrolling smoothly after load made the list visibly travel on open.
  useLayoutEffect(() => {
    if (!isOpen || hasUserScrolledRef.current) {
      return
    }

    activeItemRef.current?.scrollIntoView({
      block: 'center',
      behavior: 'instant',
    })
  }, [isOpen, activeIndex, loadedImages])

  useEffect(() => {
    if (!isOpen) {
      hasUserScrolledRef.current = false
      setRevealTimedOut(false)
      return
    }

    const timeout = setTimeout(() => {
      setRevealTimedOut(true)
    }, REVEAL_TIMEOUT_MS)

    return () => clearTimeout(timeout)
  }, [isOpen])

  function userScrollHandler() {
    hasUserScrolledRef.current = true
  }

  // The scroll lock is lifted only once the exit animation has finished, so the body
  // never becomes scrollable while the scrim is still fading out over the page.
  const handleExitComplete = useCallback(() => {
    document.body.style.overflow = ''
  }, [])

  // Safety net: if the component unmounts while still open, AnimatePresence never
  // gets to fire onExitComplete, so restore the body scroll here to avoid leaking
  // the lock. Runs on final unmount only.
  useEffect(
    () => () => {
      document.body.style.overflow = ''
    },
    [],
  )

  if (typeof document === 'undefined') {
    return null
  }

  function imageLoadHandler(item: string) {
    setLoadedImages((prev) => new Set(prev).add(item))
  }

  // The close button rises in just after the scrim settles, then leaves quickly
  // and with less travel than it entered — an exit shouldn't compete for
  // attention as focus returns to the carousel.
  const closeButtonVariants: Variants = {
    hidden: { opacity: 0, y: 24 },
    visible: {
      opacity: 1,
      y: 0,
      transition: prefersReducedMotion
        ? { duration: 0 }
        : { type: 'spring', duration: 0.5, bounce: 0, delay: 0.3 },
    },
    exit: {
      opacity: 0,
      y: 4,
      transition: prefersReducedMotion
        ? { duration: 0 }
        : { duration: 0.08, ease: 'easeIn' },
    },
  }

  return createPortal(
    <AnimatePresence onExitComplete={handleExitComplete}>
      {isOpen ? (
        <motion.div
          key="gallery-overlay"
          className="gallery-overlay fixed inset-0 z-50 bg-black/91"
          role="dialog"
          aria-modal="true"
          aria-label={t('galleryViewer')}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: prefersReducedMotion ? 0 : 0.22,
            ease: 'easeOut',
          }}
        >
          <div
            className={`flex flex-col gap-3 h-full overflow-y-auto py-[60px] transition-opacity duration-150 ${
              isPlaced ? 'opacity-100' : 'opacity-0'
            }`}
            onPointerDown={userScrollHandler}
            onTouchStart={userScrollHandler}
            onWheel={userScrollHandler}
          >
            {items.map((item, index) => {
              const isLoaded = loadedImages.has(item)

              return (
                <article
                  key={index}
                  ref={index === activeIndex ? activeItemRef : undefined}
                >
                  <div
                    className={`relative w-full overflow-hidden${
                      isLoaded ? '' : ' aspect-square'
                    }`}
                  >
                    {isLoaded ? null : (
                      <Skeleton
                        className="absolute inset-0 h-full w-full rounded-none"
                        style={{ backgroundColor: '#2a2a2a' }}
                        data-testid="gallery-image-skeleton"
                      />
                    )}
                    <img
                      alt={item}
                      className={`block w-full transition-opacity duration-300${
                        isLoaded ? ' opacity-100' : ' opacity-0'
                      }`}
                      onLoad={() => imageLoadHandler(item)}
                      {...formatImageSource(item, 512)}
                    />
                  </div>
                </article>
              )
            })}
          </div>
          <motion.div
            className="close-button-anchor fixed inset-x-0 bottom-12 flex justify-center"
            variants={closeButtonVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <Button
              className="close-button"
              variant="secondary"
              size="lg"
              data-haptic="light"
              data-analyticsid="product_card_photo_carousel_close_gallery_view"
              onClick={onClose}
            >
              {t('close')}
            </Button>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body,
  )
}
